import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import BreadcrumbsSchema from "../components/BreadcrumbsSchema";
import { useAuth } from "../auth/authStore";
import { literatureApi } from "../api/literatureApi";
import { filesApi } from "../api/filesApi";
import { useToast } from "../ui/toast/ToastProvider";
import { getSiteOrigin, joinUrl } from "../utils/seo";

const ALLOWED_ROLES = new Set(["ADMIN", "PSYCHOLOGIST", "MODERATOR"]);
const BOOK_FILE_EXTS = new Set(["pdf", "epub", "fb2", "docx", "txt", "mobi"]);
const COVER_GRADIENTS = [
  { id: "violet", gradient: "linear-gradient(145deg,#1C1630,#2E2258)" },
  { id: "green", gradient: "linear-gradient(145deg,#0D3B2A,#1A5C3E)" },
  { id: "brown", gradient: "linear-gradient(145deg,#2B1A0E,#4A2E18)" },
  { id: "black", gradient: "linear-gradient(145deg,#111,#2A2A2A)" },
  { id: "olive", gradient: "linear-gradient(145deg,#1A2B1A,#2E3E2E)" },
  { id: "wine", gradient: "linear-gradient(145deg,#2E1520,#4A2030)" },
];
const CATEGORY_TOPIC_OPTIONS = ["Тревога", "КПТ", "Травма", "Соматика", "Отношения", "Mindfulness", "Нейронауки"];
const CATEGORY_AUDIENCE_OPTIONS = ["Для клиентов", "Для психологов", "Для всех"];

const EMPTY_FORM = {
  slug: "",
  title: "",
  author: "",
  publishYear: "",
  pagesCount: "",
  language: "RU",
  shortDescription: "",
  description: "",
  coverUrl: "",
  buyUrl: "",
  difficultyLevel: "MEDIUM",
  externalStoreProvider: "",
  externalStoreBookId: "",
  topics: [],
  tags: [],
  whoFor: [],
  published: false,
  showInCatalog: false,
  price: "",
  discountPercent: 0,
  priceCurrency: "RUB",
  textVersionUrl: "",
  textVersionFormat: "",
};

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function toBool(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value == null) return fallback;
  const token = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "y"].includes(token)) return true;
  if (["false", "0", "no", "n"].includes(token)) return false;
  return fallback;
}

function toStringArray(value) {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((item) => String(item || "").trim())
          .filter(Boolean)
      )
    );
  }
  return Array.from(
    new Set(
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );
}

function fileExt(name = "") {
  const value = String(name || "").trim();
  const dot = value.lastIndexOf(".");
  if (dot < 0) return "";
  return value.slice(dot + 1).toLowerCase();
}

function formatBytes(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${Math.round(value)} Б`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} КБ`;
  return `${(value / (1024 * 1024)).toFixed(1)} МБ`;
}

function pickFileNameFromUrl(url = "", fallback = "book-file") {
  const clean = String(url || "").trim().split("#")[0].split("?")[0];
  if (!clean) return fallback;
  const chunks = clean.split("/").filter(Boolean);
  const tail = chunks[chunks.length - 1] || fallback;
  try {
    return decodeURIComponent(tail);
  } catch {
    return tail;
  }
}

function normalizeDifficulty(value) {
  const token = String(value || "")
    .trim()
    .toUpperCase();
  if (["EASY", "MEDIUM", "DEEP"].includes(token)) return token;
  return "MEDIUM";
}

function toOptionalString(value, maxLen = 1024) {
  const normalized = String(value || "").trim();
  if (!normalized) return null;
  return normalized.slice(0, maxLen);
}

function toOptionalInt(value) {
  const number = Number(String(value || "").trim());
  if (!Number.isFinite(number)) return null;
  return Math.trunc(number);
}

function toOptionalNumber(value) {
  const number = Number(String(value || "").replace(",", ".").trim());
  if (!Number.isFinite(number)) return null;
  return Number(number.toFixed(2));
}

function normalizeDiscountPercent(value) {
  const number = Number(String(value ?? "").replace(",", ".").trim());
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(99, Math.round(number)));
}

function cleanPayload(payload) {
  const output = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === "string" && !value.trim()) return;
    if (Array.isArray(value) && !value.length) return;
    output[key] = value;
  });
  return output;
}

function normalizeEditorText(value = "") {
  return String(value || "")
    .replace(/\u00A0/g, " ")
    .replace(/\r/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function escapeHtml(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function textToEditorHtml(text = "") {
  const normalized = normalizeEditorText(text);
  if (!normalized) return "<p></p>";
  const paragraphs = normalized
    .split(/\n{2,}/)
    .map((row) => row.trim())
    .filter(Boolean);
  if (!paragraphs.length) return "<p></p>";
  return paragraphs.map((row) => `<p>${escapeHtml(row).replace(/\n/g, "<br/>")}</p>`).join("");
}

function calcWordStats(text = "") {
  const words = normalizeEditorText(text)
    .split(/\s+/)
    .filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return { words, minutes };
}

function wordForm(n, forms) {
  const m = n % 100;
  if (m > 10 && m < 20) return forms[2];
  const k = n % 10;
  if (k === 1) return forms[0];
  if (k >= 2 && k <= 4) return forms[1];
  return forms[2];
}

function displayRole(role) {
  const value = String(role || "").toUpperCase();
  if (value === "ADMIN") return "Администратор";
  if (value === "PSYCHOLOGIST") return "Психолог";
  if (value === "MODERATOR") return "Модератор";
  if (value === "CLIENT") return "Клиент";
  return "Пользователь";
}

function normalizeBookPayload(raw = {}) {
  const source = raw && typeof raw === "object" && raw.item && typeof raw.item === "object" ? raw.item : raw;
  return source && typeof source === "object" ? source : {};
}

function toFormFromBook(raw = {}) {
  const item = normalizeBookPayload(raw);
  const priceValue = item.price ?? item.priceRub ?? item.priceAmount ?? "";
  const textVersionUrl =
    item.textVersionUrl ?? item.fileUrl ?? item.documentUrl ?? item.textUrl ?? item.contentUrl ?? item.bookFileUrl ?? "";
  const textVersionFormat =
    item.textVersionFormat ?? item.fileFormat ?? item.textFormat ?? item.contentFormat ?? item.bookFileFormat ?? "";

  return {
    ...EMPTY_FORM,
    slug: String(item.slug || ""),
    title: String(item.title || ""),
    author: String(item.author || ""),
    publishYear: item.publishYear == null ? "" : String(item.publishYear),
    pagesCount: item.pagesCount == null ? "" : String(item.pagesCount),
    language: String(item.language || item.lang || "RU").toUpperCase(),
    shortDescription: String(item.shortDescription || item.subtitle || ""),
    description: String(item.description || ""),
    coverUrl: String(item.coverUrl || item.imageUrl || ""),
    buyUrl: String(item.buyUrl || item.purchaseUrl || ""),
    difficultyLevel: normalizeDifficulty(item.difficultyLevel || item.difficultyLabel),
    externalStoreProvider: String(item.externalStoreProvider || item.externalStore?.provider || ""),
    externalStoreBookId: String(item.externalStoreBookId || item.externalStore?.bookId || ""),
    topics: toStringArray(item.topics),
    tags: toStringArray(item.tags),
    whoFor: toStringArray(item.whoFor),
    published: toBool(item.published, false),
    showInCatalog: toBool(item.showInCatalog, true),
    price: priceValue == null ? "" : String(priceValue),
    discountPercent: normalizeDiscountPercent(item.discountPercent ?? item.discount ?? item.salePercent),
    priceCurrency: String(item.priceCurrency || item.currency || "RUB").toUpperCase(),
    textVersionUrl: String(textVersionUrl || ""),
    textVersionFormat: String(textVersionFormat || "").toUpperCase(),
  };
}

function makeBookRoute(book) {
  const ident = book?.slug || book?.id;
  return ident == null ? "" : `/literature/books/${encodeURIComponent(String(ident))}`;
}

function deriveAccessMode(form) {
  const numericPrice = Number(String(form?.price || "").replace(",", "."));
  if (Number.isFinite(numericPrice) && numericPrice > 0) return "paid";
  return "free";
}

function pickCoverUploadUrl(payload = {}) {
  return (
    payload?.file?.url ||
    payload?.url ||
    payload?.item?.url ||
    payload?.item?.file?.url ||
    ""
  );
}

function buildFileMetaFromForm(form) {
  const url = String(form?.textVersionUrl || "").trim();
  if (!url) return null;
  const fallbackFormat = String(form?.textVersionFormat || "").trim().toLowerCase();
  const name = pickFileNameFromUrl(url, fallbackFormat ? `book.${fallbackFormat}` : "book-file");
  const ext = fileExt(name) || fallbackFormat;
  return {
    name,
    ext: ext || "file",
    size: "",
  };
}

export default function LiteratureBookEditor() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { me, role, logout } = useAuth();

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [loadingBook, setLoadingBook] = React.useState(false);
  const [loadingError, setLoadingError] = React.useState("");
  const [createdBook, setCreatedBook] = React.useState(null);
  const [coverGradient, setCoverGradient] = React.useState(COVER_GRADIENTS[0].gradient);
  const [accessMode, setAccessMode] = React.useState("free");
  const [discountPercent, setDiscountPercent] = React.useState(0);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [autosaveState, setAutosaveState] = React.useState("saved");
  const [inlineToast, setInlineToast] = React.useState({ open: false, text: "", tone: "mint" });
  const [tagDraft, setTagDraft] = React.useState("");
  const [wordStats, setWordStats] = React.useState(() => calcWordStats(""));
  const [editorSyncToken, setEditorSyncToken] = React.useState(0);
  const [uploadingCover, setUploadingCover] = React.useState(false);
  const [uploadingBookFile, setUploadingBookFile] = React.useState(false);
  const [uploadedBookFile, setUploadedBookFile] = React.useState(null);

  const titleRef = React.useRef(null);
  const subtitleRef = React.useRef(null);
  const editorBodyRef = React.useRef(null);
  const coverInputRef = React.useRef(null);
  const bookFileInputRef = React.useRef(null);
  const autosaveTimerRef = React.useRef(null);
  const inlineToastTimerRef = React.useRef(null);
  const editorSeedHtmlRef = React.useRef(textToEditorHtml(""));

  const canManage = ALLOWED_ROLES.has(String(role || "").toUpperCase());
  const editBookId = React.useMemo(
    () => String(searchParams.get("bookId") || searchParams.get("id") || "").trim(),
    [searchParams]
  );
  const isEditMode = Boolean(editBookId);

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(() => {
    const query = isEditMode ? `?bookId=${encodeURIComponent(editBookId)}` : "";
    return joinUrl(origin, `/dashboard/literature/new${query}`);
  }, [editBookId, isEditMode, origin]);

  const breadcrumbs = React.useMemo(
    () => [
      { label: "Кабинет", to: "/dashboard" },
      { label: "Литература", to: "/literature" },
      { label: isEditMode ? "Редактор книги" : "Новая книга" },
    ],
    [isEditMode]
  );

  const pageTitle = isEditMode ? "Редактор книги | Бюро Души" : "Добавление книги | Бюро Души";
  const pageDescription =
    "Редактор книги для психолога: обложка, описание, категории, теги, файл книги и параметры публикации.";

  const shortTitle = React.useMemo(() => {
    const value = String(form.title || "").trim();
    if (!value) return "Без названия";
    return value.length > 24 ? `${value.slice(0, 22)}…` : value;
  }, [form.title]);

  const activeCategories = React.useMemo(() => new Set([...asArray(form.topics), ...asArray(form.whoFor)]), [form.topics, form.whoFor]);

  const oldPriceLabel = React.useMemo(() => {
    if (accessMode !== "paid") return "—";
    const price = Number(String(form.price || "").replace(",", "."));
    const discount = Number(String(discountPercent || "0").replace(",", "."));
    if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(discount) || discount <= 0 || discount >= 100) {
      return "—";
    }
    const old = Math.round(price / (1 - discount / 100));
    return `${new Intl.NumberFormat("ru-RU").format(old)} ₽`;
  }, [accessMode, discountPercent, form.price]);

  const savedBookRoute = React.useMemo(() => makeBookRoute(createdBook), [createdBook]);

  const setAutosaveSaving = React.useCallback(() => {
    setAutosaveState("saving");
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => setAutosaveState("saved"), 1200);
  }, []);

  const showInlineToast = React.useCallback((text, tone = "mint") => {
    setInlineToast({ open: true, text, tone });
    if (inlineToastTimerRef.current) window.clearTimeout(inlineToastTimerRef.current);
    inlineToastTimerRef.current = window.setTimeout(() => {
      setInlineToast((prev) => ({ ...prev, open: false }));
    }, 2400);
  }, []);

  const autoResizeTextarea = React.useCallback((node) => {
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, 420)}px`;
  }, []);

  const syncEditorFromForm = React.useCallback(
    (nextForm) => {
      editorSeedHtmlRef.current = textToEditorHtml(nextForm.description);
      setForm(nextForm);
      setAccessMode(deriveAccessMode(nextForm));
      setDiscountPercent(normalizeDiscountPercent(nextForm.discountPercent));
      setUploadedBookFile(buildFileMetaFromForm(nextForm));
      setWordStats(calcWordStats(nextForm.description));
      setEditorSyncToken((prev) => prev + 1);
    },
    []
  );

  React.useEffect(() => {
    if (!canManage) return;
    if (!isEditMode) {
      setLoadingBook(false);
      setLoadingError("");
      setCreatedBook(null);
      syncEditorFromForm(EMPTY_FORM);
      return;
    }

    let cancelled = false;
    setLoadingBook(true);
    setLoadingError("");

    (async () => {
      try {
        let loaded = null;
        try {
          loaded = await literatureApi.getBook(editBookId);
        } catch (primaryError) {
          const mine = await literatureApi.getMyBooks({ page: 0, size: 300 });
          const rows = Array.isArray(mine?.content)
            ? mine.content
            : Array.isArray(mine?.items)
            ? mine.items
            : [];
          loaded =
            rows.find((item) => String(item?.id || "") === editBookId) ||
            rows.find((item) => String(item?.slug || "") === editBookId) ||
            null;
          if (!loaded) throw primaryError;
        }
        if (cancelled) return;
        const nextForm = toFormFromBook(loaded || {});
        syncEditorFromForm(nextForm);
      } catch (error) {
        if (cancelled) return;
        setLoadingError(error?.message || "Не удалось загрузить книгу для редактирования.");
      } finally {
        if (!cancelled) setLoadingBook(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManage, editBookId, isEditMode, syncEditorFromForm]);

  React.useEffect(() => {
    const body = editorBodyRef.current;
    if (!body) return;
    body.innerHTML = editorSeedHtmlRef.current;
  }, [editorSyncToken]);

  React.useEffect(() => {
    autoResizeTextarea(titleRef.current);
    autoResizeTextarea(subtitleRef.current);
  }, [autoResizeTextarea, form.title, form.shortDescription]);

  React.useEffect(
    () => () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
      if (inlineToastTimerRef.current) window.clearTimeout(inlineToastTimerRef.current);
    },
    []
  );

  React.useEffect(() => {
    if (!previewOpen && !sidebarOpen) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [previewOpen, sidebarOpen]);

  const setField = React.useCallback(
    (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setAutosaveSaving();
    },
    [setAutosaveSaving]
  );

  const toggleCategory = React.useCallback(
    (label, kind) => {
      if (kind === "topic") {
        setForm((prev) => {
          const current = new Set(asArray(prev.topics));
          if (current.has(label)) current.delete(label);
          else current.add(label);
          return { ...prev, topics: Array.from(current) };
        });
      } else {
        setForm((prev) => {
          const current = new Set(asArray(prev.whoFor));
          if (current.has(label)) current.delete(label);
          else current.add(label);
          return { ...prev, whoFor: Array.from(current) };
        });
      }
      setAutosaveSaving();
    },
    [setAutosaveSaving]
  );

  const addTag = React.useCallback(() => {
    const normalized = String(tagDraft || "").replace(/,/g, "").trim();
    if (!normalized) return;
    setForm((prev) => {
      const next = new Set(asArray(prev.tags));
      next.add(normalized);
      return { ...prev, tags: Array.from(next) };
    });
    setTagDraft("");
    setAutosaveSaving();
  }, [setAutosaveSaving, tagDraft]);

  const removeTag = React.useCallback(
    (value) => {
      setForm((prev) => ({ ...prev, tags: asArray(prev.tags).filter((tag) => tag !== value) }));
      setAutosaveSaving();
    },
    [setAutosaveSaving]
  );

  const handleTagKeyDown = React.useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault();
        addTag();
      }
    },
    [addTag]
  );

  const onEditorInput = React.useCallback(() => {
    const node = editorBodyRef.current;
    const text = normalizeEditorText(node?.innerText || "");
    setForm((prev) => ({ ...prev, description: text }));
    setWordStats(calcWordStats(text));
    setAutosaveSaving();
  }, [setAutosaveSaving]);

  const execEditorCommand = React.useCallback((command, value = null) => {
    document.execCommand(command, false, value);
    editorBodyRef.current?.focus();
    onEditorInput();
  }, [onEditorInput]);

  const setStatus = React.useCallback(
    (mode) => {
      const isPublished = mode === "published";
      setForm((prev) => ({
        ...prev,
        published: isPublished,
        showInCatalog: isPublished ? prev.showInCatalog : false,
      }));
      setAutosaveSaving();
    },
    [setAutosaveSaving]
  );

  const triggerCoverUpload = React.useCallback(() => {
    if (uploadingCover) return;
    coverInputRef.current?.click();
  }, [uploadingCover]);

  const onCoverFileChange = React.useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      try {
        setUploadingCover(true);
        const uploaded = await filesApi.uploadImage(file);
        const coverUrl = pickCoverUploadUrl(uploaded);
        if (!coverUrl) throw new Error("Сервер не вернул URL обложки");
        setForm((prev) => ({ ...prev, coverUrl }));
        setAutosaveSaving();
        showInlineToast("Обложка загружена", "mint");
      } catch (error) {
        toast.error(error?.message || "Не удалось загрузить обложку");
      } finally {
        setUploadingCover(false);
      }
    },
    [setAutosaveSaving, showInlineToast, toast]
  );

  const processBookFile = React.useCallback(
    async (file) => {
      if (!(file instanceof File)) return;
      const ext = fileExt(file.name);
      if (!BOOK_FILE_EXTS.has(ext)) {
        toast.error("Формат не поддерживается. Используйте PDF, EPUB, FB2, DOCX, TXT или MOBI");
        return;
      }

      try {
        setUploadingBookFile(true);
        const uploaded = await filesApi.uploadDocument(file);
        const fileUrl = pickCoverUploadUrl(uploaded);
        if (!fileUrl) throw new Error("Сервер не вернул URL файла книги");

        setForm((prev) => ({
          ...prev,
          textVersionUrl: fileUrl,
          textVersionFormat: ext.toUpperCase(),
        }));
        setUploadedBookFile({
          name: file.name,
          ext,
          size: formatBytes(file.size),
        });
        setAutosaveSaving();
        showInlineToast(`Файл «${file.name}» загружен`, "mint");
      } catch (error) {
        toast.error(error?.message || "Не удалось загрузить файл книги");
      } finally {
        setUploadingBookFile(false);
      }
    },
    [setAutosaveSaving, showInlineToast, toast]
  );

  const onBookFileChange = React.useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;
      await processBookFile(file);
    },
    [processBookFile]
  );

  const onBookFileDrop = React.useCallback(
    async (event) => {
      event.preventDefault();
      const file = event.dataTransfer?.files?.[0];
      if (!file) return;
      await processBookFile(file);
    },
    [processBookFile]
  );

  const removeBookFile = React.useCallback(() => {
    setForm((prev) => ({ ...prev, textVersionUrl: "", textVersionFormat: "" }));
    setUploadedBookFile(null);
    setAutosaveSaving();
    showInlineToast("Файл удалён", "lav");
  }, [setAutosaveSaving, showInlineToast]);

  const buildPayload = React.useCallback(
    (sourceForm, sourceAccessMode) => {
      const publish = Boolean(sourceForm.published);
      return cleanPayload({
        slug: toOptionalString(sourceForm.slug, 255),
        title: toOptionalString(sourceForm.title, 255),
        author: toOptionalString(sourceForm.author, 255),
        publishYear: toOptionalInt(sourceForm.publishYear),
        pagesCount: toOptionalInt(sourceForm.pagesCount),
        shortDescription: toOptionalString(sourceForm.shortDescription, 1000),
        description: toOptionalString(sourceForm.description, 20000),
        coverUrl: toOptionalString(sourceForm.coverUrl, 2048),
        buyUrl: toOptionalString(sourceForm.buyUrl, 2048),
        difficultyLevel: normalizeDifficulty(sourceForm.difficultyLevel),
        externalStoreProvider: toOptionalString(sourceForm.externalStoreProvider, 512),
        externalStoreBookId: toOptionalString(sourceForm.externalStoreBookId, 190),
        topics: toStringArray(sourceForm.topics),
        tags: toStringArray(sourceForm.tags),
        whoFor: toStringArray(sourceForm.whoFor),
        published: publish,
        showInCatalog: publish ? Boolean(sourceForm.showInCatalog) : false,
        price: sourceAccessMode === "paid" ? toOptionalNumber(sourceForm.price) : 0,
        discountPercent:
          sourceAccessMode === "paid" ? normalizeDiscountPercent(discountPercent) || null : null,
        priceCurrency: toOptionalString(sourceForm.priceCurrency || "RUB", 16) || "RUB",
        textVersionUrl: toOptionalString(sourceForm.textVersionUrl, 2048),
        textVersionFormat: toOptionalString(sourceForm.textVersionFormat, 32),
      });
    },
    [discountPercent]
  );

  const persistBook = React.useCallback(
    async ({ nextForm = form, nextAccessMode = accessMode, successText = "Изменения сохранены" } = {}) => {
      if (!canManage) {
        toast.error("Недостаточно прав для редактирования книг");
        return null;
      }
      if (loadingBook) return null;

      if (!String(nextForm.title || "").trim()) {
        toast.error("Укажите название книги");
        return null;
      }
      if (!String(nextForm.author || "").trim()) {
        toast.error("Укажите автора");
        return null;
      }

      const payload = buildPayload(nextForm, nextAccessMode);
      try {
        setSaving(true);
        const saved = isEditMode
          ? await literatureApi.updateBook(editBookId, payload)
          : await literatureApi.createBook(payload);

        const normalizedSaved = normalizeBookPayload(saved || {});
        setCreatedBook(normalizedSaved);

        if (!isEditMode && (normalizedSaved?.id != null || normalizedSaved?.slug)) {
          const next = new URLSearchParams(searchParams);
          next.set("bookId", String(normalizedSaved?.id ?? normalizedSaved?.slug));
          next.delete("id");
          setSearchParams(next, { replace: true });
        }

        if (normalizedSaved?.slug && !String(nextForm.slug || "").trim()) {
          setForm((prev) => ({ ...prev, slug: String(normalizedSaved.slug || "") }));
        }

        setAutosaveState("saved");
        showInlineToast(successText, "mint");
        return normalizedSaved;
      } catch (error) {
        if (Number(error?.status) === 403) {
          toast.error("Доступ backend разрешен только ролям: психолог, модератор, админ.");
        } else {
          toast.error(error?.message || "Не удалось сохранить книгу");
        }
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [
      accessMode,
      buildPayload,
      canManage,
      editBookId,
      form,
      isEditMode,
      loadingBook,
      searchParams,
      setSearchParams,
      showInlineToast,
      toast,
    ]
  );

  const onSaveClick = React.useCallback(async () => {
    await persistBook({ successText: isEditMode ? "Изменения сохранены" : "Книга создана" });
  }, [isEditMode, persistBook]);

  const onPublishToggle = React.useCallback(async () => {
    const nextPublished = !form.published;
    const prevForm = form;
    const nextForm = {
      ...form,
      published: nextPublished,
      showInCatalog: nextPublished ? form.showInCatalog : false,
    };

    setForm(nextForm);
    try {
      await persistBook({
        nextForm,
        nextAccessMode: accessMode,
        successText: nextPublished ? "Книга опубликована" : "Книга снята с публикации",
      });
    } catch {
      setForm(prevForm);
    }
  }, [accessMode, form, persistBook]);

  const onDelete = React.useCallback(async () => {
    if (typeof literatureApi.deleteBook !== "function") {
      showInlineToast("Удаление книги пока не реализовано на backend", "red");
      return;
    }
    if (!isEditMode) return;
    const confirmed = window.confirm("Удалить книгу? Это действие нельзя отменить.");
    if (!confirmed) return;

    try {
      setSaving(true);
      await literatureApi.deleteBook(editBookId);
      showInlineToast("Книга удалена", "mint");
      navigate("/literature");
    } catch (error) {
      toast.error(error?.message || "Не удалось удалить книгу");
    } finally {
      setSaving(false);
    }
  }, [editBookId, isEditMode, navigate, showInlineToast, toast]);

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        onSaveClick();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === "p") {
        event.preventDefault();
        setPreviewOpen(true);
        return;
      }
      if (event.key === "Escape") {
        setPreviewOpen(false);
        setSidebarOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onSaveClick]);

  if (!canManage) {
    return (
      <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
        <div className="dashboard-placeholder__hero">
          <h3>Доступ ограничен</h3>
          <p>Раздел редактора книг доступен только администраторам, психологам и модераторам.</p>
        </div>
        <div className="dashboard-placeholder__actions">
          <Link className="dashboard__btn dashboard__btn--ghost" to="/dashboard">
            ← Вернуться в кабинет
          </Link>
        </div>
      </section>
    );
  }

  return (
    <div className="podcasts-hub lit-book-editor-page">
      <Helmet prioritizeSeoTags>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
      </Helmet>
      <BreadcrumbsSchema items={breadcrumbs} />

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="literature-create"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={displayRole(role)}
          canOpenLiteratureEditor
          onLogout={async () => {
            await logout?.();
            navigate("/login", { replace: true });
          }}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <div className="book-editor-v3-topbar">
            <div className="book-editor-v3-topbar__mobile-left">
              <button className="book-editor-v3-burger" type="button" onClick={() => setSidebarOpen(true)} aria-label="Открыть меню">
                <span />
                <span />
                <span />
              </button>
            </div>

            <div className="book-editor-v3-breadcrumb">
              <button type="button" onClick={() => navigate("/literature")}>Литература</button>
              <span>/</span>
              <button type="button" onClick={() => navigate("/literature#my-books-section")}>Мои книги</button>
              <span>/</span>
              <strong>{shortTitle}</strong>
            </div>

            <button
              type="button"
              className={`book-editor-v3-status-pill ${form.published ? "is-published" : "is-draft"}`}
              onClick={() => setStatus(form.published ? "draft" : "published")}
              title="Переключить статус"
            >
              <span className="dot" />
              <span>{form.published ? "Опубликована" : "Черновик"}</span>
            </button>

            <div className="book-editor-v3-autosave">
              <span className={`dot ${autosaveState === "saving" ? "is-saving" : "is-saved"}`} />
              <span>{autosaveState === "saving" ? "Сохраняю…" : "Сохранено"}</span>
            </div>

            <div className="book-editor-v3-topbar__spacer" />

            <div className="book-editor-v3-topbar__actions">
              <button type="button" className="btn ghost" onClick={onDelete} disabled={saving}>Удалить</button>
              <button type="button" className="btn preview" onClick={() => setPreviewOpen(true)}>Просмотр</button>
              <button
                type="button"
                className={`btn publish ${form.published ? "is-unpublish" : ""}`}
                onClick={onPublishToggle}
                disabled={saving}
              >
                {form.published ? "Снять" : "Опубликовать"}
              </button>
            </div>
          </div>

          <main className="book-editor-v3-layout">
            <aside className="book-editor-v3-cover-panel">
              <div className="book-editor-v3-cover-label">Обложка</div>

              <button
                type="button"
                className="book-editor-v3-cover"
                style={
                  form.coverUrl
                    ? { backgroundImage: `url(${form.coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
                    : { background: coverGradient }
                }
                onClick={triggerCoverUpload}
              >
                {!form.coverUrl ? <span className="deco deco-circle" /> : null}
                {!form.coverUrl ? <span className="deco deco-fill" /> : null}
                <span className="overlay">
                  <span className="overlay-icon">↑</span>
                  <span>Загрузить обложку</span>
                </span>
                <span className="content">
                  <span className="title">{shortTitle}</span>
                  <span className="author">{String(form.author || "").trim() || "Автор"}</span>
                </span>
              </button>

              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={onCoverFileChange}
                disabled={uploadingCover}
              />

              <div className="book-editor-v3-cover-colors">
                {COVER_GRADIENTS.map((tone) => (
                  <button
                    key={tone.id}
                    type="button"
                    className={`swatch ${coverGradient === tone.gradient ? "active" : ""}`}
                    style={{ background: tone.gradient }}
                    onClick={() => {
                      setCoverGradient(tone.gradient);
                      showInlineToast("Цвет обложки изменён", "lav");
                    }}
                    aria-label={`Цвет ${tone.id}`}
                  />
                ))}
              </div>

              <div className="book-editor-v3-cover-actions">
                <button type="button" onClick={triggerCoverUpload} disabled={uploadingCover}>
                  {uploadingCover ? "Загрузка..." : "Загрузить"}
                </button>
                <button type="button" onClick={() => showInlineToast("Генерация обложки будет подключена позже", "lav")}>Сгенерировать</button>
              </div>
            </aside>

            <section className="book-editor-v3-center">
              <textarea
                ref={titleRef}
                className="book-editor-v3-title"
                rows={1}
                placeholder="Название книги…"
                value={form.title}
                onChange={(event) => setField("title", event.target.value)}
                onInput={(event) => autoResizeTextarea(event.currentTarget)}
              />

              <textarea
                ref={subtitleRef}
                className="book-editor-v3-subtitle"
                rows={1}
                placeholder="Подзаголовок или краткое описание…"
                value={form.shortDescription}
                onChange={(event) => setField("shortDescription", event.target.value)}
                onInput={(event) => autoResizeTextarea(event.currentTarget)}
              />

              <div className="book-editor-v3-divider" />

              <div className="book-editor-v3-toolbar">
                <select onChange={(event) => {
                  const value = event.target.value;
                  if (value) execEditorCommand("formatBlock", value);
                  event.target.value = "";
                }}>
                  <option value="">Стиль</option>
                  <option value="p">Абзац</option>
                  <option value="h2">Заголовок 2</option>
                  <option value="h3">Заголовок 3</option>
                  <option value="blockquote">Цитата</option>
                </select>

                <span className="sep" />

                <button type="button" onClick={() => execEditorCommand("bold")} title="Жирный">B</button>
                <button type="button" onClick={() => execEditorCommand("italic")} title="Курсив"><i>I</i></button>
                <button type="button" onClick={() => execEditorCommand("underline")} title="Подчеркнутый"><u>U</u></button>

                <span className="sep" />

                <button type="button" onClick={() => execEditorCommand("insertUnorderedList")} title="Маркированный список">•</button>
                <button type="button" onClick={() => execEditorCommand("insertOrderedList")} title="Нумерованный список">1.</button>

                <span className="sep" />

                <button type="button" onClick={() => execEditorCommand("undo")} title="Отменить">↶</button>
                <button type="button" onClick={() => execEditorCommand("redo")} title="Повторить">↷</button>
              </div>

              <div
                ref={editorBodyRef}
                className="book-editor-v3-body"
                contentEditable
                suppressContentEditableWarning
                onInput={onEditorInput}
              />

              <div className="book-editor-v3-word-counter">
                ~{wordStats.words} {wordForm(wordStats.words, ["слово", "слова", "слов"])} · ~{wordStats.minutes} мин чтения
              </div>
            </section>

            <aside className="book-editor-v3-meta">
              <section className="meta-card">
                <h3>Статус</h3>
                <div className="status-selector">
                  <button
                    type="button"
                    className={!form.published ? "active" : ""}
                    onClick={() => setStatus("draft")}
                  >
                    Черновик
                  </button>
                  <button
                    type="button"
                    className={form.published ? "active published" : ""}
                    onClick={() => setStatus("published")}
                  >
                    Опубликована
                  </button>
                </div>

                {form.published ? (
                  <div className="pub-stats">
                    <div><strong>342</strong><span>Читателей</span></div>
                    <div><strong>4.8</strong><span>Рейтинг</span></div>
                    <div><strong>18</strong><span>Отзывов</span></div>
                  </div>
                ) : null}
              </section>

              <section className="meta-card">
                <h3>Информация о книге</h3>

                <label className="meta-field">
                  <span>Автор</span>
                  <input value={form.author} onChange={(event) => setField("author", event.target.value)} placeholder="Имя автора" />
                </label>

                <div className="meta-row">
                  <label className="meta-field">
                    <span>Год издания</span>
                    <input
                      value={form.publishYear}
                      onChange={(event) => setField("publishYear", event.target.value)}
                      placeholder="2026"
                      inputMode="numeric"
                    />
                  </label>
                  <label className="meta-field">
                    <span>Страниц</span>
                    <input
                      value={form.pagesCount}
                      onChange={(event) => setField("pagesCount", event.target.value)}
                      placeholder="180"
                      inputMode="numeric"
                    />
                  </label>
                </div>

                <div className="meta-row">
                  <label className="meta-field">
                    <span>Язык</span>
                    <input value={form.language} onChange={(event) => setField("language", event.target.value.toUpperCase())} placeholder="RU" />
                  </label>
                  <label className="meta-field">
                    <span>Slug</span>
                    <input value={form.slug} onChange={(event) => setField("slug", event.target.value)} placeholder="mashina-vremeni" />
                  </label>
                </div>

                <label className="meta-field">
                  <span>Краткое описание</span>
                  <textarea
                    value={form.shortDescription}
                    onChange={(event) => setField("shortDescription", event.target.value)}
                    placeholder="Описание книги для карточки"
                  />
                </label>
              </section>

              <section className="meta-card">
                <h3>Категории</h3>
                <div className="cat-grid">
                  {CATEGORY_TOPIC_OPTIONS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={`chip ${activeCategories.has(item) ? "active" : ""}`}
                      onClick={() => toggleCategory(item, "topic")}
                    >
                      {item}
                    </button>
                  ))}
                  {CATEGORY_AUDIENCE_OPTIONS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={`chip ${activeCategories.has(item) ? "active" : ""}`}
                      onClick={() => toggleCategory(item, "audience")}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </section>

              <section className="meta-card">
                <h3>Теги</h3>
                <div className="tags-wrap" onClick={() => document.getElementById("book-tag-input")?.focus()}>
                  {asArray(form.tags).map((tag) => (
                    <span className="tag-pill" key={tag}>
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)}>×</button>
                    </span>
                  ))}
                  <input
                    id="book-tag-input"
                    value={tagDraft}
                    onChange={(event) => setTagDraft(event.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Добавить тег…"
                  />
                </div>
                <div className="meta-help">Enter или запятая — добавить тег</div>
              </section>

              <section className="meta-card">
                <h3>Файл книги</h3>

                {!uploadedBookFile ? (
                  <button
                    type="button"
                    className="file-drop"
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={onBookFileDrop}
                    onClick={() => bookFileInputRef.current?.click()}
                    disabled={uploadingBookFile}
                  >
                    <span className="file-drop-icon">⇧</span>
                    <span className="file-drop-title">{uploadingBookFile ? "Загрузка..." : "Перетащите файл сюда"}</span>
                    <span className="file-drop-sub">или нажмите для выбора</span>
                    <span className="file-formats">PDF · EPUB · FB2 · DOCX · TXT · MOBI</span>
                  </button>
                ) : (
                  <div className="uploaded-file">
                    <div className="icon">📄</div>
                    <div className="info">
                      <div className="name">{uploadedBookFile.name}</div>
                      <div className="meta">
                        {String(uploadedBookFile.ext || "").toUpperCase()}
                        {uploadedBookFile.size ? ` · ${uploadedBookFile.size}` : ""}
                      </div>
                    </div>
                    <button type="button" className="remove" onClick={removeBookFile}>×</button>
                  </div>
                )}

                <input
                  ref={bookFileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.epub,.fb2,.docx,.txt,.mobi"
                  onChange={onBookFileChange}
                  disabled={uploadingBookFile}
                />

                {uploadedBookFile ? (
                  <div className="format-grid">
                    {[
                      form.textVersionFormat || uploadedBookFile.ext?.toUpperCase() || "PDF",
                      "EPUB",
                      "FB2",
                      "MOBI",
                    ]
                      .filter(Boolean)
                      .filter((item, idx, arr) => arr.indexOf(item) === idx)
                      .map((format) => (
                        <label key={format} className={`format-item ${String(form.textVersionFormat || "").toUpperCase() === format ? "active" : ""}`}>
                          <input
                            type="radio"
                            name="book-file-format"
                            checked={String(form.textVersionFormat || "").toUpperCase() === format}
                            onChange={() => setField("textVersionFormat", format)}
                          />
                          {format}
                        </label>
                      ))}
                  </div>
                ) : null}
              </section>

              <section className="meta-card">
                <h3>Цена и доступ</h3>
                <div className="access-mode">
                  <button
                    type="button"
                    className={`access-opt ${accessMode === "free" ? "active" : ""}`}
                    onClick={() => {
                      setAccessMode("free");
                      setField("price", "");
                    }}
                  >
                    <span>🆓</span>
                    <small>Бесплатно</small>
                  </button>
                  <button
                    type="button"
                    className={`access-opt ${accessMode === "paid" ? "active" : ""}`}
                    onClick={() => setAccessMode("paid")}
                  >
                    <span>💳</span>
                    <small>Платно</small>
                  </button>
                  <button
                    type="button"
                    className={`access-opt ${accessMode === "sub" ? "active" : ""}`}
                    onClick={() => {
                      setAccessMode("sub");
                      setField("price", "");
                    }}
                  >
                    <span>🔑</span>
                    <small>Подписка</small>
                  </button>
                </div>

                {accessMode === "paid" ? (
                  <>
                    <div className="meta-row">
                      <label className="meta-field">
                        <span>Цена</span>
                        <input
                          value={form.price}
                          onChange={(event) => setField("price", event.target.value)}
                          inputMode="decimal"
                          placeholder="890"
                        />
                      </label>
                      <label className="meta-field">
                        <span>Валюта</span>
                        <input
                          value={form.priceCurrency}
                          onChange={(event) => setField("priceCurrency", event.target.value.toUpperCase())}
                          placeholder="RUB"
                        />
                      </label>
                    </div>
                    <label className="meta-field">
                      <span>Скидка, %</span>
                      <input
                        value={discountPercent}
                        onChange={(event) => setDiscountPercent(Math.max(0, Math.min(99, Number(event.target.value) || 0)))}
                        inputMode="numeric"
                        placeholder="0"
                      />
                    </label>
                    <label className="meta-field">
                      <span>Цена без скидки</span>
                      <input value={oldPriceLabel} readOnly />
                    </label>
                  </>
                ) : (
                  <div className="meta-help">Книга будет доступна без прямой оплаты.</div>
                )}

                <label className="meta-check">
                  <input
                    type="checkbox"
                    checked={Boolean(form.showInCatalog)}
                    onChange={(event) => setField("showInCatalog", event.target.checked)}
                  />
                  <span>Показывать в каталоге</span>
                </label>
              </section>

              <section className="action-card">
                <button type="button" className="primary" onClick={onSaveClick} disabled={saving || loadingBook}>
                  {saving ? "Сохраняем..." : "Сохранить изменения"}
                </button>
                <button type="button" onClick={() => setPreviewOpen(true)}>Предпросмотр</button>
                <button type="button" onClick={onPublishToggle} disabled={saving || loadingBook}>
                  {form.published ? "Снять с публикации" : "Опубликовать"}
                </button>
                <button type="button" onClick={() => showInlineToast("Экспорт будет добавлен позже", "lav")}>Экспортировать</button>
                <button type="button" className="danger" onClick={onDelete} disabled={saving}>Удалить книгу</button>
              </section>

              {loadingError ? <section className="editor-state error">{loadingError}</section> : null}
              {createdBook ? (
                <section className="editor-state success">
                  <strong>{isEditMode ? "Изменения сохранены" : "Книга сохранена"}</strong>
                  <span>ID: {createdBook.id ?? "—"}</span>
                  {savedBookRoute ? (
                    <button type="button" onClick={() => navigate(savedBookRoute)}>Открыть карточку</button>
                  ) : null}
                </section>
              ) : null}
            </aside>
          </main>
        </div>
      </div>

      {previewOpen ? (
        <div className="book-editor-v3-preview" onClick={() => setPreviewOpen(false)}>
          <div className="book-editor-v3-preview__inner" onClick={(event) => event.stopPropagation()}>
            <div className="book-editor-v3-preview__header">
              <strong>Предпросмотр книги</strong>
              <button type="button" onClick={() => setPreviewOpen(false)}>✕</button>
            </div>

            <div className="book-editor-v3-preview__body">
              <div
                className="cover"
                style={
                  form.coverUrl
                    ? { backgroundImage: `url(${form.coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
                    : { background: coverGradient }
                }
              >
                {!form.coverUrl ? <span className="cover-title">{shortTitle}</span> : null}
              </div>
              <h2>{String(form.title || "").trim() || "Без названия"}</h2>
              <p className="author">{String(form.author || "").trim() || "Автор"}</p>

              <div className="tags">
                {[...asArray(form.topics), ...asArray(form.whoFor), ...asArray(form.tags)]
                  .slice(0, 8)
                  .map((item) => (
                    <span key={`preview-${item}`}>{item}</span>
                  ))}
              </div>

              <div className="desc">
                {String(form.shortDescription || form.description || "").trim() || "Описание книги пока не добавлено."}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className={`book-editor-v3-toast ${inlineToast.open ? "show" : ""}`}>
        <span className={`dot ${inlineToast.tone}`} />
        <span>{inlineToast.text}</span>
      </div>
    </div>
  );
}
